import type {CSSProperties, SVGProps} from "react";
import {token} from "@atlaskit/tokens";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {clsx} from "~/components/table/tableUtils.tsx";
import {
    canRenderTableEmptyStatePortal,
    getTableEmptyStateOverlayStyle,
    renderTableEmptyStatePortal,
} from "~/components/table/tableEmptyStatePortal";
import {useTablePanelViewportRect} from "~/components/table/useTablePanelViewportRect";
import TableLoadErrorIllustration from "~/components/table/tableLoadErrorIllustration.svg?react";
import "~/components/table/tableLoadErrorEmptyState.css";

export function TableLoadErrorEmptyState({
    onRetry,
    className,
    illustrationProps,
    viewportBoundsElement,
}: {
    onRetry: () => void
    className?: string
    illustrationProps?: SVGProps<SVGSVGElement>
    viewportBoundsElement: HTMLElement | null
}) {
    const boundsRect = useTablePanelViewportRect(viewportBoundsElement);

    const overlayStyle: CSSProperties | undefined = getTableEmptyStateOverlayStyle({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const canPortal = canRenderTableEmptyStatePortal({
        viewportBoundsElement: viewportBoundsElement,
        boundsRect: boundsRect,
    });

    const overlay = (
        <div
            className={clsx("table-load-error", className)}
            style={overlayStyle}
            role="alert"
            aria-live="assertive"
        >
            <div className="table-load-error__inner">
                <div
                    className="table-load-error__illustration-wrap"
                    style={{color: token("color.border")}}
                >
                    <TableLoadErrorIllustration
                        aria-hidden
                        {...illustrationProps}
                        className={clsx("table-load-error__illustration", illustrationProps?.className)}
                    />
                </div>
                <h2 className="table-load-error__title" style={{color: token("color.text")}}>
                    Cannot load data
                </h2>
                <p className="table-load-error__body" style={{color: token("color.text")}}>
                    We&apos;re having trouble connecting to our database. Please check your internet connection and try
                    again.
                </p>
                <div className="table-load-error__actions">
                    <AnankeButton type="button" appearance="primary" onClick={onRetry}>
                        Retry
                    </AnankeButton>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {canPortal
                ? renderTableEmptyStatePortal({
                      overlay: overlay,
                      viewportBoundsElement: viewportBoundsElement,
                      boundsRect: boundsRect,
                  })
                : null}
            <div className="table-load-error__table-anchor" aria-hidden />
        </>
    );
}
